import { hashCanonical } from '@urbanrats/domain';
import { VisionCalibration, type NormPoint, type VehicleClass } from '@urbanrats/contracts';
import { useI18n } from '../i18n/index.ts';

export type PickMode = 'line' | 'roi' | null;

interface Props {
  draft: VisionCalibration;
  setDraft: (c: VisionCalibration) => void;
  pickMode: PickMode;
  setPickMode: (m: PickMode) => void;
  onSave: () => void;
  onCancel: () => void;
  savedHash: string | null;
}

const CLASSES: VehicleClass[] = ['car', 'bus', 'truck', 'motorcycle', 'bicycle'];

/** Operator tool (demo): line segment, ROI polygon, direction, classes and thresholds. */
export function CalibrationPanel({ draft, setDraft, pickMode, setPickMode, onSave, onCancel, savedHash }: Props) {
  const { t } = useI18n();
  const valid = VisionCalibration.safeParse(draft).success;
  const hash = valid ? hashCanonical(draft) : null;
  const toggle = (c: VehicleClass) => {
    const has = draft.includedClasses.includes(c);
    const next = has ? draft.includedClasses.filter((x) => x !== c) : [...draft.includedClasses, c];
    if (next.length) setDraft({ ...draft, includedClasses: next });
  };
  return (
    <section className="calibration" aria-label={t('cal.title')} data-testid="calibration">
      <h2>{t('cal.title')}</h2>
      <p className="note">{t('cal.note')}</p>
      <div className="cal-row">
        <button type="button" className={`ghost-btn bordered ${pickMode === 'line' ? 'on' : ''}`} onClick={() => setPickMode(pickMode === 'line' ? null : 'line')}>
          {t('cal.setLine')}
        </button>
        <button
          type="button"
          className={`ghost-btn bordered ${pickMode === 'roi' ? 'on' : ''}`}
          onClick={() => {
            if (pickMode === 'roi') setPickMode(null);
            else {
              setDraft({ ...draft, roi: [] as unknown as VisionCalibration['roi'] });
              setPickMode('roi');
            }
          }}
        >
          {pickMode === 'roi' ? t('cal.finishRoi') : t('cal.setRoi')}
        </button>
        <button
          type="button"
          className="ghost-btn bordered"
          onClick={() => setDraft({ ...draft, line: { ...draft.line, countedDirection: draft.line.countedDirection === 'A_TO_B' ? 'B_TO_A' : 'A_TO_B' } })}
        >
          {t('cal.flip')}
        </button>
      </div>
      {pickMode && <p className="note" role="status">{pickMode === 'line' ? t('cal.pickLine') : t('cal.pickRoi', { n: draft.roi.length })}</p>}
      <fieldset className="cal-classes">
        <legend>{t('cal.classes')}</legend>
        {CLASSES.map((c) => (
          <label key={c} className="check-row">
            <input type="checkbox" checked={draft.includedClasses.includes(c)} onChange={() => toggle(c)} /> {c}
          </label>
        ))}
      </fieldset>
      <label className="cal-range">
        {t('cal.minConf', { v: draft.minConfidence.toFixed(2) })}
        <input type="range" min={0.1} max={0.9} step={0.05} value={draft.minConfidence} onChange={(e) => setDraft({ ...draft, minConfidence: Number(e.target.value), tracking: { ...draft.tracking, highThresh: Math.max(Number(e.target.value), draft.tracking.lowThresh + 0.05) } })} />
      </label>
      <p className="note mono">
        {t('cal.hash')}: {hash ? hash.slice(0, 16) : '—'}
        {savedHash && hash === savedHash ? ` (${t('cal.saved')})` : ''}
      </p>
      {!valid && <p className="field-error">{t('cal.invalid')}</p>}
      <div className="cal-row">
        <button type="button" className="confirm" disabled={!valid || pickMode !== null} onClick={onSave}>
          {t('cal.save')}
        </button>
        <button type="button" className="ghost-btn bordered" onClick={onCancel}>
          {t('cal.cancel')}
        </button>
      </div>
    </section>
  );
}

export function applyPick(draft: VisionCalibration, mode: PickMode, p: NormPoint, lineClicks: number): { draft: VisionCalibration; done: boolean } {
  if (mode === 'line') {
    if (lineClicks % 2 === 0) return { draft: { ...draft, line: { ...draft.line, a: p } }, done: false };
    return { draft: { ...draft, line: { ...draft.line, b: p } }, done: true };
  }
  if (mode === 'roi') return { draft: { ...draft, roi: [...draft.roi, p] as VisionCalibration['roi'] }, done: false };
  return { draft, done: false };
}
